/**
 * BP Tickets API Service
 *
 * Proxies the Business Partner ticketing Airtable base through the People Space proxy server.
 * All requests use authenticatedFetch (Firebase auth required).
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { BpTicket, TicketCategory, TicketPriority, TicketStatus } from '../types/bpTickets';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export async function fetchBpTickets(): Promise<BpTicket[]> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/bp-tickets`);
  const data = await res.json();
  return data.tickets ?? [];
}

export async function updateBpTicketStatus(id: string, status: TicketStatus): Promise<BpTicket> {
  return patchBpTicket(id, { status });
}

export async function updateBpTicketCategory(id: string, category: TicketCategory): Promise<BpTicket> {
  return patchBpTicket(id, { category });
}

export async function updateBpTicketPriority(id: string, priority: TicketPriority): Promise<BpTicket> {
  return patchBpTicket(id, { priority });
}

/** Reassigns a ticket's Business Partner — enables covering a colleague's queue, e.g. on vacation. */
export async function updateBpTicketAssignee(id: string, assigneeEmail: string): Promise<BpTicket> {
  return patchBpTicket(id, { assigneeEmail });
}

/** Shared by every single-field ticket edit above — see PATCH /api/bp-tickets/:id. */
async function patchBpTicket(
  id: string,
  body: { status: TicketStatus } | { category: TicketCategory } | { priority: TicketPriority } | { assigneeEmail: string },
): Promise<BpTicket> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/bp-tickets/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  return data.ticket;
}

/**
 * Resolves a ticket's submitter to a Slack user id, for the "Message on Slack" deep link.
 *
 * Checks `res.ok` BEFORE parsing the body: a failure that never reaches our own route (a 404 from
 * an undeployed endpoint, a load-balancer 502, a dropped connection) has no JSON body, and parsing
 * it unconditionally would throw a raw "Unexpected token" SyntaxError instead of a readable
 * message. A real error from our route still surfaces its specific `error` string.
 */
export async function fetchTicketSlackContact(id: string): Promise<{ slackUserId: string }> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/bp-tickets/${encodeURIComponent(id)}/slack-contact`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}) as { error?: string });
    throw new Error(data.error || 'Could not reach the server — please try again.');
  }
  return res.json();
}
